/**
 * @lodex-pro/web-shared/components/ui/status-label — a status shown as a coloured dot plus a
 * sentence-case word ("Paid", "Failed", "On"), in place of the small uppercase pills (web
 * restyle, Corbin 2026-10-28). Display only: the value it is given (a stored enum such as PAID
 * or PAST_DUE) is never changed, only shown as `statusWord(value)`.
 *
 * The tone keeps the colour's meaning: success (paid, available, on), warning (due soon, busy,
 * draft), danger (failed, overdue, offline), info (in progress), neutral (off, void, not started).
 * The word is in the text colour; the dot alone carries the hue, so the neutral grey (under
 * 4.5:1 as text) is never text.
 */
import * as React from 'react';
import { cn } from 'src/lib/utils';

export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const DOT: Readonly<Record<StatusTone, string>> = {
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger)',
  info: 'var(--color-link)',
  neutral: 'var(--color-neutral)',
};

/**
 * PAST_DUE → "Past due"; "in review" → "In review". A label already in mixed case is kept, and so
 * is a short code ("OK", "TX").
 */
export function statusWord(value: string): string {
  const v = value.replace(/_/g, ' ').trim();
  if (v !== v.toUpperCase() && v !== v.toLowerCase()) return v;
  if (/^[A-Z0-9]{1,3}$/.test(v)) return v;
  const lower = v.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export interface StatusLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  readonly tone: StatusTone;
  /** The status: a stored value (PAID) or a label ("Verified"). Shown in sentence case. */
  readonly children: string;
}

export function StatusLabel({
  tone,
  children,
  className,
  style,
  ...props
}: StatusLabelProps): React.JSX.Element {
  return (
    <span
      className={cn('inline-flex items-center gap-2 whitespace-nowrap', className)}
      style={{ color: 'var(--color-text-primary)', ...style }}
      {...props}
    >
      <span
        aria-hidden="true"
        style={{
          width: '9px',
          height: '9px',
          borderRadius: '50%',
          flex: 'none',
          backgroundColor: DOT[tone],
        }}
      />
      {statusWord(children)}
    </span>
  );
}
